import React, { useMemo, useState, useEffect, useRef } from "react";
import { Button } from "react-bootstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { statusConditions } from "../Footer";
import { toast } from 'react-toastify';
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";


const formatDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const ExpandableText = ({ text, maxLength = 100 }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!text) return <span>—</span>;
  
  if (text.length <= maxLength || expanded) {
    return (
      <span>
        {text}
        {text.length > maxLength && (
          <Button 
            variant="link" 
            size="sm" 
            onClick={() => setExpanded(false)}
            className="p-0 ms-1 view-more"
            style={{ fontSize: '12px' }}
          >
            View Less
          </Button>
        )}
      </span>
    );
  }
  
  const truncatedText = text.substring(0, maxLength) + '...';
  return (
    <span>
      {truncatedText}
      <Button 
        variant="link" 
        size="sm" 
        onClick={() => setExpanded(true)}
        className="p-0 ms-1 view-more"
        style={{ fontSize: '12px' }}
      >
        View More
      </Button>
    </span>
  );
};

const useSpeech = (rate = 0.9) => {
  const speechQueue = useRef([]);
  const isSpeaking = useRef(false);
  const currentUtterance = useRef(null);

  const speak = (text) => {
    if (!text) return;
    
    if (isSpeaking.current && currentUtterance.current) {
      window.speechSynthesis.cancel();
    }
    
    speechQueue.current = [text];
    isSpeaking.current = false;
    processQueue();
  };

  const cancelSpeech = () => {
    if (isSpeaking.current) {
      window.speechSynthesis.cancel();
      isSpeaking.current = false;
      speechQueue.current = [];
    }
  };

  const processQueue = () => {
    if (speechQueue.current.length === 0) {
      isSpeaking.current = false;
      return;
    }

    isSpeaking.current = true;
    const text = speechQueue.current.shift();
    const synth = window.speechSynthesis;
    const voices = synth.getVoices();
    const femaleVoice = voices.find(v =>
      v.lang.toLowerCase().includes("en") &&
      (v.name.toLowerCase().includes("zira") || v.name.toLowerCase().includes("female"))
    ) || voices.find(v => v.lang === "en-US");

    currentUtterance.current = new SpeechSynthesisUtterance(text);
    currentUtterance.current.voice = femaleVoice;
    currentUtterance.current.lang = "en-US";
    currentUtterance.current.rate = rate;
    
    currentUtterance.current.onend = () => {
      setTimeout(processQueue, 300);
    };

    synth.speak(currentUtterance.current);
  };

  return { speak, cancelSpeech };
};

const parseSpokenDate = (dateText) => {
  if (!dateText) return '';
  
  if (dateText.includes('today')) return new Date().toISOString().split('T')[0];
  if (dateText.includes('tomorrow')) {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  }
  if (dateText.includes('next week')) {
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    return nextWeek.toISOString().split('T')[0];
  }
  
  const months = {
    january: '01', february: '02', march: '03', april: '04', may: '05', june: '06',
    july: '07', august: '08', september: '09', october: '10', november: '11', december: '12',
    jan: '01', feb: '02', mar: '03', apr: '04', jun: '06',
    jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12'
  };
  
  const dateMatch = dateText.match(/(\d{1,2})\s+(\w+)\s+(\d{4})/) || 
                   dateText.match(/(\w+)\s+(\d{1,2})\s+(\d{4})/) ||
                   dateText.match(/(\d{1,2})\s+(\w+)/) ||
                   dateText.match(/(\w+)\s+(\d{1,2})/);
  
  if (dateMatch) {
    let day, month, year;
    
    if (dateMatch[3]) { 
      if (months[dateMatch[1].toLowerCase()]) { 
        month = months[dateMatch[1].toLowerCase()];
        day = dateMatch[2];
        year = dateMatch[3];
      } else { 
        day = dateMatch[1];
        month = months[dateMatch[2].toLowerCase()];
        year = dateMatch[3];
      }
    } else { 
      year = new Date().getFullYear();
      if (months[dateMatch[1].toLowerCase()]) { 
        month = months[dateMatch[1].toLowerCase()];
        day = dateMatch[2];
      } else { 
        day = dateMatch[1];
        month = months[dateMatch[2].toLowerCase()];
      }
    }
    
    if (month && day) {
      return `${year}-${month}-${day.padStart(2, '0')}`;
    }
  }
  
  const numericMatch = dateText.match(/(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})/);
  if (numericMatch) {
    const day = numericMatch[1].padStart(2, '0');
    const month = numericMatch[2].padStart(2, '0');
    let year = numericMatch[3];
    if (year.length === 2) year = `20${year}`;
    return `${year}-${month}-${day}`;
  }
  
  const dayOnlyMatch = dateText.match(/\b(\d{1,2})\b/);
  if (dayOnlyMatch) {
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    return `${year}-${month}-${dayOnlyMatch[1].padStart(2, '0')}`;
  }
  
  return dateText;
};

const cleanSpeechText = (text) => {
  let cleaned = text.toLowerCase()
                   .replace(/[.,]/g, '')
                   .replace(/\s+/g, ' ')
                   .trim();
  
  const replacements = {
    'date of': 'date',
    'decision date': 'date',
    'action taken': 'status',
    'additional comments': 'comments',
    'task id': 'task',
    'priority level': 'priority',
    'search for': '',
    'filter by': '',
    'show me': '',
    'next week': 'nextweek',
    'this week': 'thisweek',
    'last week': 'lastweek'
  };

  Object.entries(replacements).forEach(([from, to]) => {
    cleaned = cleaned.replace(new RegExp(from, 'g'), to);
  });

  return cleaned;
};

const TaskTable = ({
  tasks,
  isLoading,
  onEditTask,
  onDeleteTask,
  onShowDeadlineHistory,
  onShowCoordinatorComments,
  onShowOfficerComments,
  onShowAttachments,
  onShowActionComments,
  roleName,
  userName,
}) => {
  const { speak, cancelSpeech } = useSpeech();
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    taskID: { value: null, matchMode: FilterMatchMode.EQUALS },
    priorityName: { value: null, matchMode: FilterMatchMode.EQUALS },
    coordinatorNames: { value: null, matchMode: FilterMatchMode.CONTAINS },
    officerNames: { value: null, matchMode: FilterMatchMode.CONTAINS },
    issueActionOrDecision: { value: null, matchMode: FilterMatchMode.CONTAINS },
    dateOfDecisionOrInitiation: { value: null, matchMode: FilterMatchMode.CONTAINS },
    deadline: { value: null, matchMode: FilterMatchMode.CONTAINS },
    actionTakenOrStatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
    additionalComments: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });

  const [listening, setListening] = useState(false);
  const [activeFilter, setActiveFilter] = useState(null);
  const recognitionRef = useRef(null);

  const alertTimeoutRef = useRef(null);

  const hasSearchParameters = (query) => {
  if (!query || query.trim() === '' || query === 'Listening...') {
    return false;
  }
  
  const lowerQuery = query.toLowerCase().trim();
  const searchKeywords = [
    'task id', 'priority', 'status', 'category', 
    'group', 'task coordinator', 'date', 'deadline'
  ];
  
  return searchKeywords.some(keyword => lowerQuery.includes(keyword));
};
const showWarningAlert = (message) => {
  if (alertTimeoutRef.current) {
    clearTimeout(alertTimeoutRef.current);
  }
  
  toast.warning(message, {
    toastId: 'search-warning'
  });
  
  alertTimeoutRef.current = setTimeout(() => {
    // Clear any existing warnings after 3 seconds
  }, 3000);
};

  useEffect(() => {
    if ('webkitSpeechRecognition' in window) {
      recognitionRef.current = new window.webkitSpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        const cleanedText = cleanSpeechText(transcript);
        handleVoiceCommand(cleanedText);
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error', event.error);
        setListening(false);
        setActiveFilter(null);
      };

      recognitionRef.current.onend = () => {
        setListening(false);
        setActiveFilter(null);
      };
    }
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const handleEditWithSpeech = (taskID) => {
    const task = tasks.find(t => t.taskID === taskID);
    if (task) {
      const message = `Editing Task ID ${task.taskID}`;
      speak(message);
    }
    onEditTask(taskID);
  };

  const handleVoiceCommand = (command) => {

     if (!hasSearchParameters(command) && 
      !command.includes('help') && 
      !command.includes('clear') && 
      !command.includes('reset') && 
      !command.includes('edit task')) {
    showWarningAlert("Please specify search parameters like Task ID, Priority, Status etc.");
    return;
  }

    if (command.includes('help')) {
      speak("Available commands: filter by task ID, priority, coordinator, officer, issue, date, deadline, status, or comments. " +
            "Say 'clear filters' to reset. Say 'edit task' followed by the task ID to edit a task.");
      return;
    }
    
    if (command.includes('clear filters') || command.includes('reset filters')) {
      setFilters({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        taskID: { value: null, matchMode: FilterMatchMode.EQUALS },
        priorityName: { value: null, matchMode: FilterMatchMode.EQUALS },
        coordinatorNames: { value: null, matchMode: FilterMatchMode.CONTAINS },
        officerNames: { value: null, matchMode: FilterMatchMode.CONTAINS },
        issueActionOrDecision: { value: null, matchMode: FilterMatchMode.CONTAINS },
        dateOfDecisionOrInitiation: { value: null, matchMode: FilterMatchMode.CONTAINS },
        deadline: { value: null, matchMode: FilterMatchMode.CONTAINS },
        actionTakenOrStatus: { value: null, matchMode: FilterMatchMode.CONTAINS },
        additionalComments: { value: null, matchMode: FilterMatchMode.CONTAINS },
      });
      speak("All filters cleared");
      return;
    }

    const editMatch = command.match(/edit task (\d+)/i);
    if (editMatch) {
      const taskID = parseInt(editMatch[1]);
      if (tasks.some(t => t.taskID === taskID)) {
        handleEditWithSpeech(taskID);
      } else {
        speak(`Task ID ${taskID} not found`);
      }
      return;
    }

    const newFilters = JSON.parse(JSON.stringify(filters));

    if (activeFilter) {
      switch (activeFilter) {
        case 'taskID':
          const idMatch = command.match(/(\d+)/);
          if (idMatch) {
            newFilters.taskID.value = idMatch[1];
            speak(`Filtering by task ID ${idMatch[1]}`);
          }
          break;
        case 'priorityName':
          const priorities = ['top', 'high', 'medium', 'low'];
          const foundPriority = priorities.find(p => command.includes(p));
          if (foundPriority) {
            newFilters.priorityName.value = foundPriority.charAt(0).toUpperCase() + foundPriority.slice(1);
            speak(`Filtering by ${foundPriority} priority`);
          }
          break;
        case 'coordinatorNames':
          newFilters.coordinatorNames.value = command;
          speak(`Filtering by coordinator ${command}`);
          break;
        case 'officerNames':
          newFilters.officerNames.value = command;
          speak(`Filtering by officer ${command}`);
          break;
        case 'issueActionOrDecision':
          newFilters.issueActionOrDecision.value = command;
          speak(`Searching issue/action for "${command}"`);
          break;
        case 'dateOfDecisionOrInitiation':
          const parsedDate = parseSpokenDate(command);
          newFilters.dateOfDecisionOrInitiation.value = parsedDate;
          speak(`Filtering by date "${command}"`);
          break;
        case 'deadline':
          const parsedDeadline = parseSpokenDate(command);
          newFilters.deadline.value = parsedDeadline;
          speak(`Filtering by deadline "${command}"`);
          break;
        case 'actionTakenOrStatus':
          newFilters.actionTakenOrStatus.value = command;
          speak(`Filtering by status "${command}"`);
          break;
        case 'additionalComments':
          newFilters.additionalComments.value = command;
          speak(`Filtering comments for "${command}"`);
          break;
        default:
          speak(`Command not recognized for this filter.`);
          break;
      }
      
      setFilters(newFilters);
      setActiveFilter(null);
      return;
    }

    const idMatch = command.match(/task\s*(\d+)/i);
    if (idMatch) {
      newFilters.taskID.value = idMatch[1];
      speak(`Filtering by task ID ${idMatch[1]}`);
    }
    
    const priorities = ['top', 'high', 'medium', 'low'];
    const foundPriority = priorities.find(p => command.includes(p));
    if (foundPriority) {
      newFilters.priorityName.value = foundPriority.charAt(0).toUpperCase() + foundPriority.slice(1);
      speak(`Filtering by ${foundPriority} priority`);
    }
    
    if (command.includes('issue') || command.includes('action')) {
      const searchText = command.replace(/(issue|action|search|filter)/gi, '').trim();
      if (searchText) {
        newFilters.issueActionOrDecision.value = searchText;
        speak(`Searching issue/action for "${searchText}"`);
      }
    }
    
    if (command.includes('date') && !command.includes('deadline')) {
      debugger
      const dateText = command.replace(/(date|of|decision)/gi, '').trim();
      if (dateText) {
        const parsedDate = parseSpokenDate(dateText);
        newFilters.dateOfDecisionOrInitiation.value = parsedDate;
        speak(`Filtering by date "${dateText}"`);
      }
    }
    
    if (command.includes('deadline')) {
      debugger
      const deadlineText = command.replace(/deadline/gi, '').trim();
      if (deadlineText) {
        const parsedDate = parseSpokenDate(deadlineText);
        newFilters.deadline.value = parsedDate;
        speak(`Filtering by deadline "${deadlineText}"`);
      }
    }
    
    if (command.includes('status')) {
      const statusText = command.replace(/status/gi, '').trim();
      if (statusText) {
        newFilters.actionTakenOrStatus.value = statusText;
        speak(`Filtering by status "${statusText}"`);
      }
    }
    
    if (command.includes('comment')) {
      const commentText = command.replace(/(comment|comments)/gi, '').trim();
      if (commentText) {
        newFilters.additionalComments.value = commentText;
        speak(`Filtering comments for "${commentText}"`);
      }
    }
    
    const nameMatch = command.match(/(coordinator|officer)\s+(\w+)/i);
    if (nameMatch) {
      const [_, type, name] = nameMatch;
      if (type.toLowerCase() === 'coordinator') {
        newFilters.coordinatorNames.value = name;
        speak(`Filtering by coordinator ${name}`);
      } else {
        newFilters.officerNames.value = name;
        speak(`Filtering by officer ${name}`);
      }
    }

    
    setFilters(newFilters);
  };

  useEffect(() => {
  return () => {
    if (alertTimeoutRef.current) {
      clearTimeout(alertTimeoutRef.current);
    }
  };
}, []);
  

  const toggleListeningForFilter = (filterName) => {
    if (listening && activeFilter === filterName) {
      recognitionRef.current.stop();
      setListening(false);
      setActiveFilter(null);
      speak("Voice input cancelled");
    } else {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setActiveFilter(filterName);
      speak(`Listening for ${filterName.replace(/([A-Z])/g, ' $1').toLowerCase()} filter...`);
      recognitionRef.current.start();
      setListening(true);
    }
  };

  const filterOptions = useMemo(() => ({
    taskID: [...new Set(tasks.map(task => task.taskID))].sort((a, b) => a - b),
    priorityName: [...new Set(tasks.map(task => task.priorityName))].filter(Boolean).sort((a, b) => {
      const priorityOrder = { "Top": 1, "High": 2, "Medium": 3, "Low": 4 };
      return priorityOrder[a] - priorityOrder[b];
    }),
    coordinatorNames: [...new Set(tasks.flatMap(task => task.coordinatorNames?.split(', ') || []))].filter(Boolean).sort(),
    officerNames: [...new Set(tasks.flatMap(task => task.officerNames?.split(', ') || []))].filter(Boolean).sort(),
  }), [tasks]);

  const commonHeaderStyle = {
    backgroundColor: '#04364A',
    color: 'white',
    fontSize: '14px',
    fontWeight: '700',
  padding: '14px 4px',
  };

  const filterInputStyle = {
    width: '100%',
  padding: '4px',
  boxSizing: 'border-box',
  backgroundColor: 'transparent',
  height: '41px',
  padding: '3px 8px',
  fontSize: '14px',
  lineHeight: '15px',
  textTransform: 'capitalize',
  whiteSpace: 'nowrap',  
  };

// Filter input style (consistent for both dropdown and text filters)
const fixedFilterInputStyle = {
  width: '100%',
  padding: '4px',
  boxSizing: 'border-box'
};

// Dropdown filter template
const dropdownFilterTemplate = (
  options,
  values,
  placeholder,
  filterName,
  minColumnWidth = 30
) => {
  const selectedLabel =
    values.find(v => v.value === options.value)?.label || placeholder;

  return (
    <div
      className="p-inputgroup"
      style={{
        width: 'auto',
        minWidth: `${minColumnWidth}px`,
        maxWidth: '100%'
      }}
    >
      <Dropdown
        value={options.value}
        options={values}
        onChange={(e) => options.filterApplyCallback(e.value)}
        placeholder={placeholder}
        showClear
        className="p-column-filter no-ellipsis"
        style={{
          ...filterInputStyle,
          width: '100%'
        }}
      />
      <Button
        size="sm"
        className="micphone-btn"
        onClick={() => toggleListeningForFilter(filterName)}
        title="Voice input"
        style={{ padding: '4px' }}
      >
        <i
          className={`bi bi-mic${
            listening && activeFilter === filterName ? '-fill' : ''
          }`}
        />
      </Button>
    </div>
  );
};



// Input text filter template
const inputFilterTemplate = (options, placeholder, filterName, columnWidth) => (
  <div
    className="p-inputgroup"
    style={{
      width: columnWidth,
      minWidth: columnWidth,
      maxWidth: columnWidth
    }}
  >
    <InputText
      value={options.value || ''}
      onChange={(e) => options.filterApplyCallback(e.target.value)}
      placeholder={placeholder}
      className="p-column-filter"
      style={filterInputStyle}
      
    />
    <Button
      size="sm"
      className="micphone-btn"
      onClick={() => toggleListeningForFilter(filterName)}
      title="Voice input"
      style={{ padding: '4px' , background:'transparent' }}
    >
      <i
        className={`bi bi-mic${
          listening && activeFilter === filterName ? '-fill' : ''
        }`}
      />
    </Button>
  </div>
);


const priorityBodyTemplate = (rowData) => {
  let bg = "transparent", color = "#1e293b"; // updated bg to transparent
  switch (rowData.priorityName?.toLowerCase()) {
    case "top": color = "#92400e"; break;
    case "high": color = "#7f1d1d"; break;
    case "medium": color = "#1e3a8a"; break;
  }
  return (
    <span style={{
      display: "inline-block",
      padding: "4px 12px",
      borderRadius: "999px",
      backgroundColor: bg, // now transparent
      color: color,
      fontWeight: "600",
      fontSize: "13px",
      textTransform: "capitalize",
      width: "100px",
      textAlign: "center",
    }}>
      {rowData.priorityName}
    </span>
  );
};


const statusBodyTemplate = (rowData) => {
  const icon =
    rowData.status === "Significant Delay" ? statusConditions[4].icon :
    rowData.status === "Slight Delay" ? statusConditions[3].icon :
    rowData.status === "On Time" ? statusConditions[2].icon :
    rowData.status === "Completed" ? statusConditions[0].icon :
    statusConditions[1].icon;

  return (
    <img
      src={`${process.env.PUBLIC_URL}/icons/${icon}`}
      style={{ width: "40px", height: "40px" }}
      alt={`Status ${rowData.status}`}
      title={rowData.status} 
    />
  );
};

  const actionBodyTemplate = (rowData) => (
    <div className="d-flex gap-2">
     
      <Button 
        variant="outline-warning custompadding" 
        size="sm" 
        onClick={() => handleEditWithSpeech(rowData.taskID)} 
        title="Edit task"
      >
        <i className="bi bi-pencil-fill edit-pencilicon"></i>
      </Button>
      {(roleName === "Admin" || roleName === "SuperAdmin" || (roleName === "TaskCoordinator" && userName === rowData.coordinatorNames)) && (
        <Button variant="outline-danger custompadding" size="sm" onClick={() => onDeleteTask(rowData.taskID)} title="Delete task">
          <i className="bi bi-trash-fill"></i>
        </Button>
      )}
    </div>
  );

  const dateColumnTemplate = (rowData, field) => (
    <div style={{ minWidth: '80px' }}>
      {formatDate(rowData[field])}
    </div>
  );

  const deadlineBodyTemplate = (rowData) => {
    const hasDeadline = rowData.deadlineCount > 0;
    return (
      <div  style={{ minWidth: '80px' }}>
        <span
          onClick={() => hasDeadline && onShowDeadlineHistory(rowData.taskID)}
          className={hasDeadline ? "mediumcolor cursor-pointer" : ""}
        >
          {formatDate(rowData.deadline)}
        </span>
         <span>
        {hasDeadline && (
          <i className="bi bi-clock-history ms-2 text-muted cursor-pointer mediumcolor"
            onClick={() => onShowDeadlineHistory(rowData.taskID)}
            title="View deadline history" />
        )}
        </span>
      </div>
    );
  };

  const actionStatusBodyTemplate = (rowData) => {
    const hasComments = rowData.actionCount > 0;
    return (
      <div
        className={`d-flex  ${hasComments ? "cursor-pointer" : ""}`}
        onClick={() => hasComments && onShowActionComments(rowData)}
        style={{ minWidth: '167px' }}
      >
        <ExpandableText text={rowData.actionTakenOrStatus} maxLength={30} />
        {hasComments && <i className="bi bi-chat-text ms-2 text-muted mediumcolor" />}
      </div>
    );
  };

  const officerBodyTemplate = (rowData) => {
    const hasAttachments = rowData.attachmentCount > 0;
    return (
      <div className="d-flex " style={{ minWidth: '150px' }}>
        <span>{rowData.officerNames || "--"}</span>
        {hasAttachments && (
          <i className="bi bi-paperclip ms-2 text-muted cursor-pointer mediumcolor"
            onClick={(e) => { e.stopPropagation(); onShowAttachments(rowData); }}
            title="View attachments" />
        )}
      </div>
    );
  };

  const commentsBodyTemplate = (rowData) => {
    const hasComments = rowData.remarksCount > 0;
    return (
      <div className="d-flex " style={{ minWidth: '150px' }}>
        <ExpandableText text={rowData.additionalComments} maxLength={30} />
        {hasComments && (
          <i className="bi bi-chat-text ms-2 text-muted cursor-pointer mediumcolor"
            onClick={(e) => { e.stopPropagation(); onShowOfficerComments(rowData); }}
            title="View comments" />
        )}
      </div>
    );
  };

  return (
    <>
 
<DataTable
  value={tasks}
  paginator
  rows={10}
  scrollable
  loading={isLoading}
  dataKey="taskID"
  filters={filters}
  filterDisplay="row"
  showGridlines
  className=" custom-data-table  first-dashboard-table"
  rowClassName={(_rowData, index) => index % 2 === 0 ? 'even-row' : 'odd-row'}


>

<Column
  field="taskID"
  header="Task ID"
  filter
  showFilterMenu={false}
  filterElement={(options) =>
    inputFilterTemplate(options, "search", "taskID",'80px')
  }
  headerStyle={commonHeaderStyle}
  headerClassName="with-border center-align"
  bodyClassName="with-border center-align text-divcenter"
  
/>


  <Column
    field="issueActionOrDecision"
    header="Issue/Action/Decision"
    filter
    showFilterMenu={false}
    filterElement={(options) =>
      inputFilterTemplate(options, "Search", "issueActionOrDecision",)
    }
    style={{ minWidth: '170px' }}
 

    headerStyle={commonHeaderStyle}
   
    body={(rowData) => (
      <div style={{ minWidth: '170px' , textAlign:"left" }}>
        <ExpandableText text={rowData.issueActionOrDecision} maxLength={50} />
      </div>
    )}
  />

  <Column
  field="priorityName"
  header="Priority"
  filter
  showFilterMenu={false}
  body={priorityBodyTemplate}
  filterElement={(options) =>
    dropdownFilterTemplate(
      options,
      filterOptions.priorityName,
      "Select",
      "priorityName",
      '100px'
    )
  }
  style={{ textAlign: "center" }}
    headerStyle={commonHeaderStyle}
  bodyClassName="with-border center-aligncenter"
headerClassName="with-border center-alignscenter"
  />

  <Column
    field="dateOfDecisionOrInitiation"
    header="Decision Date"
    filter
    showFilterMenu={false}
    body={(row) => dateColumnTemplate(row, 'dateOfDecisionOrInitiation')}
    filterElement={(options) =>
      inputFilterTemplate(options, "Search  ", "dateOfDecisionOrInitiation",'100px')
    }
    style={{  minWidth: '100px' , textAlign: 'center' }}
    headerStyle={commonHeaderStyle}
  bodyClassName="with-border center-alignpriority"
headerClassName="with-border center-alignspriority"
  />

<Column
  field="deadline"
  header="Deadline"
  filter
  showFilterMenu={false}
  body={deadlineBodyTemplate}
  filterElement={(options) =>
    inputFilterTemplate(options, "Search ", "deadline",'120px')
  }
  style={{ minWidth: '120px', textAlign: 'center' }}
  headerStyle={{ ...commonHeaderStyle, textAlign: 'center' }}
  headerClassName="with-border center-aligns"
  bodyClassName="with-border center-align text-divcenter2"
/>


  <Column
    field="coordinatorNames"
    header="Task Coordinators"
    filter
    showFilterMenu={false}
    filterElement={(options) =>
      dropdownFilterTemplate(options, filterOptions.coordinatorNames, "Select", "coordinatorNames")
    }
    style={{  minWidth: '100px', textAlign: 'center' }}
    headerStyle={commonHeaderStyle}
    headerClassName="with-border center-aligncenter"
    bodyClassName="with-border center-alignscenter"
  />

<Column
  field="actionTakenOrStatus"
  header="Action Taken/Status"
  filter
  showFilterMenu={false}
  body={actionStatusBodyTemplate}
  filterElement={(options) =>
    inputFilterTemplate(options, "Search", "actionTakenOrStatus")
  }
  style={{ minWidth: '100px' }}
  headerStyle={commonHeaderStyle}
  headerClassName="with-border center-align"
  bodyClassName="with-border"
 />


  <Column
    field="officerNames"
    header="Officers"
    filter
    showFilterMenu={false}
    body={officerBodyTemplate}
    filterElement={(options) =>
      dropdownFilterTemplate(options, filterOptions.officerNames, "Select", "officerNames")
    }
    style={{ minWidth: '100px', textAlign: 'left' }}
    headerStyle={commonHeaderStyle}
    headerClassName="with-border center-align"
    bodyClassName="with-border center-align"
  />

  <Column
    field="additionalComments"
    header="Additional Comments"
    filter
    showFilterMenu={false}
    className="custom-searchoption"
    body={commentsBodyTemplate}
    filterElement={(options) =>
      inputFilterTemplate(options, "Search ", "additionalComments")
    }
    style={{minWidth: '100px', textAlign: 'left' }}
    headerStyle={commonHeaderStyle}
    headerClassName="with-border center-align"
    bodyClassName="with-border center-align"
  />

  <Column
    field="status"
    header="Status"
    body={statusBodyTemplate}
    style={{ minWidth: '100px', textAlign: 'center' }}
    headerStyle={commonHeaderStyle}
    headerClassName="with-border center-align2"
    bodyClassName="with-border center-align2"
  />

  <Column
    header="Actions"
    body={actionBodyTemplate}
    style={{ minWidth: '100px', textAlign: 'center' }}
    headerStyle={commonHeaderStyle}
    headerClassName="with-border center-align"
    bodyClassName="with-border center-align"
    
  />
</DataTable>
</>
   

  );
};

export default TaskTable;